import { useState, useMemo } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { createMember, updateMember, updateMemberEmail, removeMember, exportMembers } from '../services/ApiService';
import type { Member } from '../types/models';
import Modal from '../components/Modal';
import MemberImportModal from '../components/MemberImportModal';
import { Plus, Users, Shield, User, Trash2, ChevronUp, ChevronDown, Pencil, PieChart, Download, Upload } from 'lucide-react';
import { AnalyticsEvents, track } from '../services/AnalyticsService';

interface EditForm {
  memberId: string;
  userId: string;
  fullName: string;
  email: string;
  originalEmail: string;
  isPending: boolean;
  phone: string;
  propertyNumber: string;
  sharePercentage: string;
  role: string;
}

export default function MembersPage() {
  const { currentOrg, isBoardMember, members, refreshMembers } = useOrganization();
  const { user } = useAuth();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [form, setForm] = useState({ email: '', fullName: '', phone: '', propertyNumber: '', sharePercentage: '', role: 'member' });
  const [editForm, setEditForm] = useState<EditForm>({ memberId: '', userId: '', fullName: '', email: '', originalEmail: '', isPending: false, phone: '', propertyNumber: '', sharePercentage: '', role: 'member' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editError, setEditError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'property' | 'share'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const totalShare = useMemo(() => {
    return members.reduce((sum, m) => sum + (m.sharePercentage || 0), 0);
  }, [members]);

  const handleExport = async () => {
    if (!currentOrg) return;
    try {
      await exportMembers(currentOrg.id);
    } catch {
      alert('Kunde inte exportera medlemmarna.');
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg) return;
    setError('');
    setSaving(true);

    try {
      await createMember(currentOrg.id, {
        email: form.email,
        fullName: form.fullName,
        phone: form.phone,
        propertyNumber: form.propertyNumber,
        sharePercentage: parseFloat(form.sharePercentage) || 0,
        role: form.role,
      });
      track(AnalyticsEvents.MemberAdded, { orgId: currentOrg.id, role: form.role });
      setShowAddModal(false);
      setForm({ email: '', fullName: '', phone: '', propertyNumber: '', sharePercentage: '', role: 'member' });
      refreshMembers();
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : '';
      if (raw.includes('redan med')) {
        setError('Den här e-postadressen är redan registrerad som medlem i föreningen.');
      } else if (raw.includes('felkod') || !raw) {
        setError('Det gick inte att skicka inbjudan. Kontrollera att uppgifterna är korrekta och försök igen.');
      } else {
        setError(raw);
      }
    }
    setSaving(false);
  };

  const handleEditMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg) return;
    setEditError('');
    setSaving(true);

    try {
      if (editForm.isPending && editForm.email !== editForm.originalEmail) {
        await updateMemberEmail(currentOrg.id, editForm.memberId, editForm.email);
      }
      await updateMember(currentOrg.id, editForm.memberId, {
        fullName: editForm.fullName,
        phone: editForm.phone,
        propertyNumber: editForm.propertyNumber,
        sharePercentage: parseFloat(editForm.sharePercentage) || 0,
        role: editForm.role,
      });
      setShowEditModal(false);
      refreshMembers();
      if (editForm.isPending && editForm.email !== editForm.originalEmail) {
        setSuccessMessage(`En ny inbjudan har skickats till ${editForm.email}.`);
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : '';
      if (raw.includes('används redan')) {
        setEditError('Den här e-postadressen används redan av en annan användare.');
      } else {
        setEditError(raw || 'Något gick fel');
      }
    }
    setSaving(false);
  };

  const openEditModal = (m: Member) => {
    setEditForm({
      memberId: m.id,
      userId: m.userId,
      fullName: m.fullName ?? '',
      email: m.email ?? '',
      originalEmail: m.email ?? '',
      isPending: m.isPending,
      phone: m.phone ?? '',
      propertyNumber: m.propertyNumber ?? '',
      sharePercentage: String(m.sharePercentage ?? 0),
      role: m.role,
    });
    setEditError('');
    setShowEditModal(true);
  };

  const handleRemoveMember = async (m: Member) => {
    if (!currentOrg || m.userId === user?.id) return;
    if (!window.confirm(`Är du säker på att du vill ta bort ${m.fullName} från föreningen?`)) return;
    try {
      await removeMember(currentOrg.id, m.id);
      refreshMembers();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Något gick fel');
    }
  };

  const toggleSort = (col: 'name' | 'property' | 'share') => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const sorted = [...members].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortBy === 'name') {
      return (a.fullName ?? '').localeCompare(b.fullName ?? '') * dir;
    }
    if (sortBy === 'share') {
      return ((a.sharePercentage || 0) - (b.sharePercentage || 0)) * dir;
    }
    return (a.propertyNumber ?? '').localeCompare(b.propertyNumber ?? '') * dir;
  });

  const SortIcon = ({ col }: { col: 'name' | 'property' | 'share' }) => {
    if (sortBy !== col) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />;
  };

  const shareIsBalanced = Math.abs(totalShare - 100) < 0.01;

  return (
    <div className="max-w-5xl mx-auto p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Medlemmar</h1>
          <p className="text-gray-500 text-sm mt-1">{members.length} medlemmar i föreningen</p>
        </div>
        {isBoardMember && (
          <div className="flex items-center gap-2">
            <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-medium text-sm transition-colors">
              <Download className="w-4 h-4" />
              Exportera
            </button>
            <button onClick={() => setShowImportModal(true)} className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-medium text-sm transition-colors">
              <Upload className="w-4 h-4" />
              Importera
            </button>
            <button data-tour="member-add" onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm transition-colors">
              <Plus className="w-4 h-4" />
              Lägg till medlem
            </button>
          </div>
        )}
      </div>

      {successMessage && (
        <div className="mb-5 bg-emerald-50 text-emerald-700 px-4 py-3 rounded-xl text-sm border border-emerald-100">
          {successMessage}
        </div>
      )}

      {members.length > 0 && (
        <div className={`flex items-center gap-4 mb-5 px-5 py-3.5 rounded-xl border ${shareIsBalanced ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          <PieChart className={`w-5 h-5 flex-shrink-0 ${shareIsBalanced ? 'text-emerald-600' : 'text-amber-600'}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between">
              <p className={`text-sm font-semibold ${shareIsBalanced ? 'text-emerald-800' : 'text-amber-800'}`}>
                Andelstal totalt
              </p>
              <p className={`text-lg font-bold tabular-nums ${shareIsBalanced ? 'text-emerald-700' : 'text-amber-700'}`}>
                {totalShare.toFixed(2)} %
              </p>
            </div>
            <div className="mt-1.5 h-2 bg-white/60 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${shareIsBalanced ? 'bg-emerald-500' : totalShare > 100 ? 'bg-red-500' : 'bg-amber-500'}`}
                style={{ width: `${Math.min(totalShare, 100)}%` }}
              />
            </div>
            {!shareIsBalanced && (
              <p className="text-xs mt-1 text-amber-600">
                {totalShare < 100
                  ? `${(100 - totalShare).toFixed(2)} % saknas för att nå 100 %`
                  : `${(totalShare - 100).toFixed(2)} % över 100 %`}
              </p>
            )}
          </div>
        </div>
      )}

      {members.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Inga medlemmar ännu</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <button onClick={() => toggleSort('name')} className="col-span-3 flex items-center gap-1 text-left">
              Namn <SortIcon col="name" />
            </button>
            <div className="col-span-2">E-post</div>
            <button onClick={() => toggleSort('property')} className="col-span-2 flex items-center gap-1 text-left">
              Fastighet <SortIcon col="property" />
            </button>
            <button onClick={() => toggleSort('share')} className="col-span-2 flex items-center gap-1 text-left">
              Andelstal <SortIcon col="share" />
            </button>
            <div className="col-span-2">Roll</div>
            <div className="col-span-1"></div>
          </div>
          <div className="divide-y divide-gray-50">
            {sorted.map(m => {
              const share = m.sharePercentage || 0;
              return (
                <div key={m.id} className="md:grid md:grid-cols-12 gap-4 px-5 py-3.5 items-center hover:bg-gray-50/80 transition-colors">
                  <div className="col-span-3 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${m.role === 'board_member' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600'}`}>
                      {m.fullName?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{m.fullName}</p>
                      {m.phone && <p className="text-xs text-gray-400">{m.phone}</p>}
                    </div>
                  </div>
                  <div className="col-span-2 text-sm text-gray-600 truncate hidden md:flex items-center gap-1.5">
                    <span className="truncate">{m.email}</span>
                    {m.isPending && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 flex-shrink-0" title="Har inte accepterat inbjudan än">
                        Väntande
                      </span>
                    )}
                  </div>
                  <div className="col-span-2 text-sm text-gray-600 hidden md:block">{m.propertyNumber || '-'}</div>
                  <div className="col-span-2 hidden md:block">
                    <span className="text-sm font-medium text-gray-900 tabular-nums">{share.toFixed(2)} %</span>
                  </div>
                  <div className="col-span-2 hidden md:block">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${m.role === 'board_member' ? 'bg-teal-50 text-teal-700' : 'bg-gray-50 text-gray-600'}`}>
                      {m.role === 'board_member' ? <><Shield className="w-3 h-3" /> Styrelse</> : <><User className="w-3 h-3" /> Medlem</>}
                    </span>
                  </div>
                  <div className="col-span-1 flex justify-end gap-1 mt-2 md:mt-0">
                    {isBoardMember && (
                      <button onClick={() => openEditModal(m)} className="p-1.5 rounded-lg hover:bg-teal-50 transition-colors" title="Redigera">
                        <Pencil className="w-4 h-4 text-gray-400 hover:text-teal-600" />
                      </button>
                    )}
                    {isBoardMember && m.userId !== user?.id && (
                      <button onClick={() => handleRemoveMember(m)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Ta bort">
                        <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-t border-gray-200 bg-gray-50/50">
            <div className="col-span-3 text-sm font-semibold text-gray-700">Totalt ({members.length} st)</div>
            <div className="col-span-2"></div>
            <div className="col-span-2"></div>
            <div className="col-span-2">
              <span className={`text-sm font-bold tabular-nums ${shareIsBalanced ? 'text-emerald-700' : 'text-amber-700'}`}>
                {totalShare.toFixed(2)} %
              </span>
            </div>
            <div className="col-span-3"></div>
          </div>
        </div>
      )}

      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Bjud in medlem">
        <form onSubmit={handleAddMember} className="space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-100">{error}</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fullständigt namn</label>
              <input type="text" value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">E-post</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
              <p className="text-xs text-gray-400 mt-1">En inbjudningslänk skickas till denna adress</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefon</label>
              <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fastighetsnummer</label>
              <input type="text" value={form.propertyNumber} onChange={e => setForm({ ...form, propertyNumber: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Andelstal (%)</label>
              <input type="number" value={form.sharePercentage} onChange={e => setForm({ ...form, sharePercentage: e.target.value })} min="0" max="100" step="0.01" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Roll</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none">
                <option value="member">Medlem</option>
                <option value="board_member">Styrelsemedlem</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={saving} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50 transition-colors">
              {saving ? 'Skickar inbjudan...' : 'Skicka inbjudan'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Redigera medlem">
        <form onSubmit={handleEditMember} className="space-y-4">
          {editError && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-100">{editError}</div>
          )}
          <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
            <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-semibold">
              {editForm.fullName.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div>
              <p className="font-medium text-gray-900">{editForm.fullName}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label htmlFor="edit-member-name" className="block text-sm font-medium text-gray-700 mb-1.5">Namn</label>
              <input id="edit-member-name" type="text" value={editForm.fullName} onChange={e => setEditForm({ ...editForm, fullName: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">E-post</label>
              {editForm.isPending ? (
                <>
                  <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
                  <p className="text-xs text-gray-400 mt-1">Medlemmen har inte accepterat sin inbjudan än. Om du ändrar e-postadressen skickas en ny inbjudan.</p>
                </>
              ) : (
                <input type="email" value={editForm.email} disabled className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-400 outline-none" />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefon</label>
              <input type="text" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fastighetsnummer</label>
              <input type="text" value={editForm.propertyNumber} onChange={e => setEditForm({ ...editForm, propertyNumber: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Andelstal (%)</label>
              <input type="number" value={editForm.sharePercentage} onChange={e => setEditForm({ ...editForm, sharePercentage: e.target.value })} min="0" max="100" step="0.01" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Roll</label>
              <select
                value={editForm.role}
                onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                disabled={editForm.userId === user?.id}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="member">Medlem</option>
                <option value="board_member">Styrelsemedlem</option>
              </select>
              {editForm.userId === user?.id && (
                <p className="text-xs text-gray-400 mt-1">Du kan inte ändra din egen roll</p>
              )}
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={saving} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50 transition-colors">
              {saving ? 'Sparar...' : 'Spara ändringar'}
            </button>
          </div>
        </form>
      </Modal>
      {currentOrg && (
        <MemberImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          orgId={currentOrg.id}
          onImported={refreshMembers}
        />
      )}
    </div>
  );
}
